import { TableCell } from "@mui/material";
import { DotLegalLink, DotLegalOverflowMenu, DotLegalTable, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import EntityTypeIconWithName from "@common/components/entityTypeIcon/EntityTypeIconWithName";
import TableRow from "@common/components/tableRow/TableRow";
import { RiskLevelChipStyle } from "../../riskSettings/riskArea/riskAreaDialog/riskLevels/riskLevelIndicators";
import RiskLevelChip from "../RiskLevelChip";
import { RiskEntityRow } from "./RiskGovernanceOverview.types";

interface RiskGovernanceEntityTableProps {
    rows: Array<RiskEntityRow>;
    getRiskChipStyle: (level: number) => RiskLevelChipStyle | null;
    isLoading: boolean;
    onNavigate: (row: RiskEntityRow) => void;
    getEntityUrl: (row: RiskEntityRow) => string;
}

function RiskGovernanceEntityTable({ rows, getRiskChipStyle, isLoading, onNavigate, getEntityUrl }: RiskGovernanceEntityTableProps) {
    const { translateString } = useTranslation();

    const headers: Array<ITableHeader<RiskEntityRow>> = [
        { property: "typeLabel", text: translateString("type") },
        { property: "name", text: translateString("name") },
        { property: "riskLabel", text: translateString("risk") },
        { property: "responsibleName", text: translateString("responsible") },
    ];

    const renderRiskChip = (level: number, label: string) => {
        const style = getRiskChipStyle(level);
        return style && <RiskLevelChip level={level} label={label} color={style.color} icon={style.icon} />;
    };

    return (
        <DotLegalTable
            headers={headers}
            defaultOrderBy={"riskLevel"}
            defaultOrder={"desc"}
            data={rows}
            isLoading={isLoading}
            hidePagination
            disableFilterAndSortAndSlicing
            hasOverflowMenu
            emptyText={translateString("noData")}
            clickableRows
            renderRow={(row) => {
                // Two rows have no assessment page to open, and neither the row, the name nor the overflow menu
                // then navigates: an entity a snapshot still lists after it was removed from the area, and an
                // organisation scenario, whose assessments live in a section of the operational tab.
                const rowNavigates = row.canNavigate;
                return (
                    <TableRow
                        key={`${row.subject.kind}-${row.id}`}
                        onClick={rowNavigates ? () => onNavigate(row) : undefined}
                        sx={{ cursor: rowNavigates ? "pointer" : "default" }}
                    >
                        <TableCell>
                            {row.subject.kind === "entity" ? <EntityTypeIconWithName type={row.subject.entityType} name={row.typeLabel} /> : row.typeLabel}
                        </TableCell>
                        <TableCell>
                            {rowNavigates ? (
                                <DotLegalLink to={getEntityUrl(row)} linkColor="primary" bold stopPropagation>
                                    {row.name}
                                </DotLegalLink>
                            ) : (
                                row.name
                            )}
                        </TableCell>
                        <TableCell>{renderRiskChip(row.riskLevel, row.riskLabel)}</TableCell>
                        <TableCell>{row.responsibleName}</TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                            {rowNavigates && (
                                <DotLegalOverflowMenu
                                    items={[{ menuItem: translateString("riskGovernanceGoToAssessments"), onClick: () => onNavigate(row) }]}
                                />
                            )}
                        </TableCell>
                    </TableRow>
                );
            }}
        />
    );
}

export default RiskGovernanceEntityTable;
